import { DashboardLayout, Spinner, TemplatesDashboardIcon } from '@moovly/ui';
import modules from '../modules';
import * as React from 'react';
import { t } from '@moovly/i18n';
import { BackofficeRoute } from '../types/route.interfaces';
import TopRight from '../components/DashboardLayout/TopRight';
import { OAuth } from '@moovly/clients';

import { Link } from '@moovly/dashboard-global';
import { useWhitelabel } from '@moovly/whitelabel';
import { ZohoContainer } from '@moovly/zoho';

import useCurrentUser from '../hooks/useCurrentUser';
import useDashboardStatus from '../hooks/useDashboardStatus';
import NotAllowed from '../components/NotAllowed';
import { UserSearchInput } from '../modules/users';
import GroupSettingsProvider from '../modules/settings/container/GroupSettingsProvider';
import { useSettingUpdate } from '@moovly/moovly-settings';

interface DashboardContainerProps {
  children: React.ReactNode;
}

const DashboardContainer: React.FC<DashboardContainerProps> = ({ children }) => {
  const currentUser = useCurrentUser();
  const { themingConfig } = useWhitelabel();
  const { loading, hasAccess } = useDashboardStatus();
  const { value: settingsLogoUrl } = useSettingUpdate<string>('dashboard_customisation.logo');

  const menuItems = React.useMemo(() => {
    return [
      ...Object.values(modules).flatMap((module) => {
        const formatSidebarRoutes = (routes: BackofficeRoute[]): any => {
          return routes
            .filter((r) => !!r.sidebar)
            .map((route) => ({
              route: route.path,
              icon: route.sidebar!.icon,
              label: t(route.sidebar!.translationKey),
              subItems: route.children && formatSidebarRoutes(route.children),
            }));
        };
        return formatSidebarRoutes(typeof module.routes === 'function' ? module.routes(currentUser) : module.routes);
      }),
      {
        route: 'https://dashboard.moovly.com',
        icon: TemplatesDashboardIcon,
        isExternalLink: true,
        label: t('menu.dashboard'),
      },
    ];
  }, [currentUser]);

  const handleLogout = React.useCallback(() => {
    OAuth.logout();
  }, []);

  const email = React.useMemo(() => {
    return currentUser.user && currentUser.user.active_email.email;
  }, [currentUser]);

  if (loading) return <Spinner />;
  if (!hasAccess) return <NotAllowed />;

  return (
    <GroupSettingsProvider>
      <DashboardLayout
        linkComponent={Link}
        logo={settingsLogoUrl || themingConfig?.logo}
        logoIndicator="Backoffice"
        menuItems={menuItems}
        leftTop={<UserSearchInput />}
        rightTop={email && <TopRight email={email} onLogout={handleLogout} />}
      >
        {children}
      </DashboardLayout>
      {currentUser.user && currentUser.license && (
        <ZohoContainer profile={currentUser.user} license={currentUser.license} alwaysShowChat />
      )}
    </GroupSettingsProvider>
  );
};

export default DashboardContainer;
